import styled, { css } from 'styled-components';
import { Screens } from 'core/CssVariables';
import {
  AUTH_CARD_WIDTH,
  AuthFlowColors,
  AuthFlowFont,
} from './authFlow.constants';

/**
 * The white 520px card used on every auth-flow screen.
 * Individual screens supply their own inner content + gap.
 */
export const SAuthCard = styled.div`
  width: ${AUTH_CARD_WIDTH}px;
  max-width: 100%;
  background: ${AuthFlowColors.white};
  border: 1px solid ${AuthFlowColors.cardBorder};
  border-radius: 12px;
  padding: 32px;
  font-family: ${AuthFlowFont};

  @media (max-width: ${Screens.ScreensSM}) {
    padding: 24px;
  }
`;

/**
 * Scoped form styling shared across the flow's cards: labels above inputs,
 * required asterisk after the label (red), 44px inputs, navy primary button.
 * Apply inside a card via `${authCardFormCss}`.
 */
export const authCardFormCss = css`
  .ant-form-item {
    margin-bottom: 16px;
  }
  .ant-form-item:last-child {
    margin-bottom: 0;
  }
  .ant-form-item-label {
    padding-bottom: 6px;
  }
  .ant-form-item-label > label {
    height: auto;
    font: 500 14px/16px ${AuthFlowFont};
    color: ${AuthFlowColors.navyText};
  }
  /* Move the required asterisk to AFTER the label, recolour it. */
  .ant-form-item-label > label.ant-form-item-required::before {
    display: none !important;
  }
  .ant-form-item-label > label.ant-form-item-required::after {
    display: inline-block;
    content: '*';
    margin-left: 2px;
    color: ${AuthFlowColors.error};
    font-size: 12px;
    line-height: 1;
  }

  .ant-input,
  .ant-input-affix-wrapper {
    min-height: 44px;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid ${AuthFlowColors.cardBorder};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    font: 400 14px/24px ${AuthFlowFont};
    color: ${AuthFlowColors.navyText};
  }
  .ant-input-affix-wrapper > .ant-input {
    min-height: auto;
    padding: 0;
    border: none;
    box-shadow: none;
  }
  .ant-input::placeholder {
    color: ${AuthFlowColors.placeholder};
  }
  .ant-input:hover,
  .ant-input-affix-wrapper:hover,
  .ant-input:focus,
  .ant-input-affix-wrapper-focused {
    border-color: ${AuthFlowColors.navy};
  }
  .ant-form-item-has-error .ant-input,
  .ant-form-item-has-error .ant-input-affix-wrapper {
    border-color: ${AuthFlowColors.errorBorder} !important;
    border-width: 2px;
  }
  .ant-form-item-explain-error {
    margin-top: 4px;
    font: 500 12px/16px ${AuthFlowFont};
    color: ${AuthFlowColors.error};
  }

  .ant-btn-primary {
    height: 44px;
    border-radius: 8px;
    background: ${AuthFlowColors.navy};
    border-color: ${AuthFlowColors.navy};
    font: 600 16px/24px ${AuthFlowFont};
    box-shadow: none;
  }
  .ant-btn-primary:hover,
  .ant-btn-primary:focus {
    background: ${AuthFlowColors.navyHover};
    border-color: ${AuthFlowColors.navyHover};
  }
  .ant-btn-primary[disabled],
  .ant-btn-primary[disabled]:hover {
    background: ${AuthFlowColors.navy};
    border-color: ${AuthFlowColors.navy};
    color: ${AuthFlowColors.white};
    opacity: 0.85;
  }

  /* Suppress the app-wide "* Required" form legend (GlobalStyles) inside the
     auth-flow cards — the new design doesn't use it. */
  .ant-form::after {
    display: none !important;
    content: none !important;
  }
`;

/** "Already have an account? Sign in" style secondary row/link. */
export const authSecondaryLinkCss = css`
  font: 600 14px/16px ${AuthFlowFont};
  color: ${AuthFlowColors.placeholder};
  text-align: center;
  a {
    color: ${AuthFlowColors.link};
    font-weight: 600;
  }
`;

/**
 * Shared form card for the auth-flow screens (sign up, login, reset, invite).
 * One source of truth for the card skeleton — header/title/subtitle, the form
 * styling, the full-width button group, and the secondary link — so screens
 * don't re-author (and drift) these blocks or import each other's feature
 * styles. Screen-specific extras extend this via `styled(SAuthFormCard)`.
 * Uses generic `auth-card__*` class names.
 */
export const SAuthFormCard = styled(SAuthCard)`
  display: flex;
  flex-direction: column;
  gap: 32px;

  .auth-card__header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  .auth-card__title {
    margin: 0;
    font: 700 24px/1 ${AuthFlowFont};
    color: ${AuthFlowColors.navyText};
  }
  .auth-card__subtitle {
    margin: 0;
    font: 400 16px/24px ${AuthFlowFont};
    color: ${AuthFlowColors.textSecondary};
  }
  .auth-card__subtitle strong {
    color: ${AuthFlowColors.navyText};
    font-weight: 600;
  }

  ${authCardFormCss}

  /* Terms checkbox (sign up / invitation) */
  .ant-checkbox-wrapper {
    align-items: flex-start;
    font: 500 12px/16px ${AuthFlowFont};
    color: ${AuthFlowColors.navyText};
  }
  .ant-checkbox-wrapper a {
    color: ${AuthFlowColors.link};
    font-weight: 500;
  }

  .auth-card__button-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .auth-card__button-group .ant-btn {
    width: 100%;
  }
  .auth-card__secondary {
    ${authSecondaryLinkCss}
  }
`;
